// General Report — superadmin-only view of scan volume over time for one club.
// See GENERAL_REPORT_PLAN.md.
//
// Phase 1 deliberately requires a single club: "All clubs" is phase 2, so the
// club picker has no "All" option and Run stays disabled until one is chosen.
//
// The page never calls axios itself — every number comes through
// generalReportAdapter.fetchSection(), which is also where the mock lives. Each
// of the three sections keeps its own {loading, error, data} so one failing
// section never blanks the other two.
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import {
    Box, Paper, Typography, Grid, Card, CardContent, Button, TextField,
    Autocomplete, CircularProgress,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { useUrlContext } from '../context/urlContext';
import { errorContext } from '../Components/ErrorDisplay';
import ScanTrendChart from '../Components/charts/ScanTrendChart';
import { createSlotAllocator } from '../Components/charts/vizPalette';
import { SECTIONS, PASS_TYPES, fetchSection } from './generalReportAdapter';

// Format a Date as "YYYY-MM-DD" from local parts, so the picker's day is the day
// that gets sent (toISOString would shift it in western timezones).
function ymd(date) {
    if (!date || Number.isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

const emptySection = () => ({ loading: false, error: false, errorMessage: '', data: null });

const SECTION_ORDER = [SECTIONS.passType, SECTIONS.location, SECTIONS.promotion];

export default function GeneralReport() {
    const { UrlHelperObj } = useUrlContext();
    const { handleError, notify } = useContext(errorContext);

    const today = new Date();
    const fourWeeksAgo = new Date();
    fourWeeksAgo.setDate(today.getDate() - 28);

    const [clubs, setClubs] = useState([]);
    const [selectedClub, setSelectedClub] = useState(null);
    const [startDate, setStartDate] = useState(fourWeeksAgo);
    const [endDate, setEndDate] = useState(today);
    const [hasRun, setHasRun] = useState(false);

    const [sections, setSections] = useState({
        passType: emptySection(),
        location: emptySection(),
        promotion: emptySection(),
    });

    // One colour allocator per dimension, kept for the life of the page so a
    // location keeps its colour when the date range changes. Refs, not state:
    // allocating must not cause a render.
    const allocators = useRef({
        // Pass types are semantic, not data-driven — pin them so Membership is
        // always blue, Attached always orange, Branded always aqua.
        passType: createSlotAllocator({ membership: 0, attached: 1, branded: 2 }),
        location: createSlotAllocator(),
        promotion: createSlotAllocator(),
    });
    const [colorMaps, setColorMaps] = useState({ passType: {}, location: {}, promotion: {} });

    const datesValid = startDate && endDate && !Number.isNaN(startDate.getTime())
        && !Number.isNaN(endDate.getTime()) && startDate <= endDate;
    const canRun = Boolean(selectedClub) && datesValid;

    // --- Club list. No "All clubs" entry: phase 1 is single-club only. ---
    useEffect(() => {
        axios.get(UrlHelperObj.partnerUrl())
            .then(res => res.data)
            .then(rows => {
                setClubs(Array.isArray(rows) ? rows.map(c => ({ id: c.id, name: c.name })) : []);
            })
            .catch(err => handleError(err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadSection = useCallback(async (sectionId, params) => {
        setSections(prev => ({
            ...prev,
            [sectionId]: { ...prev[sectionId], loading: true, error: false },
        }));
        try {
            const data = await fetchSection(sectionId, params, UrlHelperObj);
            // Allocate colours before painting, so the chart and its legend agree.
            const map = allocators.current[sectionId](data.orderedKeys);
            setColorMaps(prev => ({ ...prev, [sectionId]: map }));
            setSections(prev => ({
                ...prev,
                [sectionId]: { loading: false, error: false, errorMessage: '', data },
            }));
        } catch (err) {
            // Show the real reason inline. While the endpoints are being brought
            // up this is the difference between "something broke" and "the server
            // returned `data` where the contract says `series`".
            const detail = (err && err.response && err.response.status)
                ? `HTTP ${err.response.status}${err.response.statusText ? ` ${err.response.statusText}` : ''}`
                : (err && err.message) || 'Unknown error';
            setSections(prev => ({
                ...prev,
                [sectionId]: { loading: false, error: true, errorMessage: detail, data: null },
            }));
            handleError(err);
        }
    }, [UrlHelperObj, handleError]);

    function currentParams() {
        return {
            issuerId: selectedClub ? selectedClub.id : 0,
            startDate: ymd(startDate),
            endDate: ymd(endDate),
        };
    }

    function handleRun() {
        if (!selectedClub) {
            notify('Please select a club.', 'warning');
            return;
        }
        if (!datesValid) {
            notify('Please choose a valid date range (start on or before end).', 'warning');
            return;
        }
        setHasRun(true);
        const params = currentParams();
        SECTION_ORDER.forEach(s => loadSection(s.id, params));
    }

    function handleClear() {
        setSelectedClub(null);
        setStartDate(fourWeeksAgo);
        setEndDate(today);
        setSections({
            passType: emptySection(),
            location: emptySection(),
            promotion: emptySection(),
        });
        setHasRun(false);
    }

    const anyLoading = SECTION_ORDER.some(s => sections[s.id].loading);

    // --- KPI row: window totals per pass type, in the fixed semantic order. ---
    function renderKpis() {
        const state = sections.passType;
        const byKey = {};
        ((state.data && state.data.allSeries) || []).forEach(s => { byKey[s.key] = s.total; });

        return (
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {PASS_TYPES.map(type => (
                    <Grid item xs={12} sm={4} key={type.key}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Box sx={{
                                        width: 10, height: 10, borderRadius: '2px', flexShrink: 0,
                                        bgcolor: colorMaps.passType[type.key] || 'transparent',
                                    }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {type.label}
                                    </Typography>
                                </Box>
                                {state.loading ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                        {state.error ? '—' : (byKey[type.key] || 0).toLocaleString()}
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                    scans in range
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Paper sx={{ p: '8px 12px', mb: 2 }}>
                <Typography variant="h5" sx={{ textAlign: 'left' }}>General Report</Typography>
            </Paper>

            {/* Filter bar — club and date range drive all three sections. */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <Autocomplete
                            options={clubs}
                            value={selectedClub}
                            getOptionLabel={(o) => o.name || ''}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            onChange={(e, val) => setSelectedClub(val)}
                            renderInput={(params) => (
                                <TextField {...params} label="Club" size="small" required />
                            )}
                        />
                    </Grid>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid item xs={6} sm={4} md={2}>
                            <DatePicker
                                label="From date"
                                value={startDate}
                                inputFormat="dd/MM/yyyy"
                                maxDate={endDate || undefined}
                                onChange={setStartDate}
                                renderInput={(params) => (
                                    <TextField {...params} size="small" fullWidth InputLabelProps={{ shrink: true }} />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <DatePicker
                                label="To date"
                                value={endDate}
                                inputFormat="dd/MM/yyyy"
                                minDate={startDate || undefined}
                                onChange={setEndDate}
                                renderInput={(params) => (
                                    <TextField {...params} size="small" fullWidth InputLabelProps={{ shrink: true }} />
                                )}
                            />
                        </Grid>
                    </LocalizationProvider>
                    <Grid item xs={12} md={5}>
                        <Button
                            variant="contained" color="primary" onClick={handleRun}
                            disabled={!canRun || anyLoading} sx={{ mr: 1 }}
                        >
                            Run
                        </Button>
                        <Button variant="outlined" color="secondary" onClick={handleClear}>
                            Clear
                        </Button>
                    </Grid>
                </Grid>
                {!hasRun && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                        Select a club and date range, then click <strong>Run</strong>.
                    </Typography>
                )}
            </Paper>

            {renderKpis()}

            {SECTION_ORDER.map(section => (
                <ScanTrendChart
                    key={section.id}
                    title={section.title}
                    entityLabel={section.entityLabel}
                    data={sections[section.id].data}
                    loading={sections[section.id].loading}
                    error={sections[section.id].error}
                    errorMessage={sections[section.id].errorMessage}
                    colorMap={colorMaps[section.id]}
                    onRetry={() => loadSection(section.id, currentParams())}
                />
            ))}
        </Box>
    );
}
